/**
 * M List frontend filter mapping (FRONTEND-ONLY).
 * Derives value, badge, and philanthropy filter options from existing listing tags.
 * No backend changes. Tag matching is by slug/name (case-insensitive).
 */

/** Value-based filter definitions. Match listing tags by slug or name. */
export const VALUE_FILTERS = [
  { id: "women-owned", label: "Women+ owned", tagSlugs: ["women-owned", "women-led", "womenowned"] },
  { id: "mom-owned", label: "Mom owned", tagSlugs: ["mom-owned", "mama-owned", "mamaowned"] },
  { id: "bipoc-owned", label: "BIPOC owned", tagSlugs: ["bipoc-owned", "bipoc", "poc-owned"] },
  { id: "black-owned", label: "Black owned", tagSlugs: ["black-owned", "blackowned"] },
  { id: "aapi-owned", label: "AAPI owned", tagSlugs: ["aapi-owned", "aapi", "asian-owned"] },
  { id: "lgbtqia-owned", label: "LGBTQIA+ owned", tagSlugs: ["lgbtqia-owned", "lgbtq-owned", "lgbtq", "lgbtqia"] },
];

/** Badge / authority filter definitions. */
export const BADGE_FILTERS = [
  { id: "collective", label: "Collective member", tagSlugs: ["collective", "tmc", "collective-member", "mamahood-collective"] },
  { id: "recommended", label: "Mamahood-recommended faves", tagSlugs: ["recommended", "fave", "favorites", "mamahood-fave", "staff-pick"] },
];

/** Philanthropy / community filter definitions. */
export const PHILANTHROPY_FILTERS = [
  { id: "nonprofit", label: "Nonprofit / 501(c)(3)", tagSlugs: ["nonprofit", "501c3", "501-c-3"] },
  { id: "give-back", label: "Community give back", tagSlugs: ["give-back", "philanthropy", "community-give-back", "community-giveback"] },
];

/**
 * Normalize a tag identifier for matching (slug or name from API).
 * @param {string} s
 * @returns {string}
 */
function norm(s) {
  if (typeof s !== "string") return "";
  return s.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

/**
 * Get all tag slugs (and normalized names) for a listing using a tag map.
 * API may return listing.tags as IDs or as objects { id, name, slug }.
 * @param {Object} listing - Listing from API (has tags: array of IDs or objects)
 * @param {Object} tagIdToTag - Map of tag id -> { id, name, slug } from groupings
 * @returns {string[]}
 */
export function getListingTagSlugs(listing, tagIdToTag = {}) {
  const raw = listing?.tags ?? [];
  const slugs = [];
  raw.forEach((t) => {
    if (typeof t === "object" && t !== null) {
      if (t.slug) slugs.push(norm(t.slug));
      if (t.name) slugs.push(norm(t.name));
    } else if (typeof t === "number" || typeof t === "string") {
      const tag = tagIdToTag[t];
      if (tag) {
        if (tag.slug) slugs.push(norm(tag.slug));
        if (tag.name) slugs.push(norm(tag.name));
      }
    }
  });
  return [...new Set(slugs)];
}

/**
 * Check if listing matches a value filter (by tag slug/name).
 */
export function listingMatchesValueFilter(listing, valueId, tagIdToTag) {
  const def = VALUE_FILTERS.find((v) => v.id === valueId);
  if (!def) return false;
  const listingSlugs = getListingTagSlugs(listing, tagIdToTag);
  const matchSlugs = def.tagSlugs.map(norm);
  return matchSlugs.some((ms) => listingSlugs.some((ls) => ls.includes(ms) || ms.includes(ls)));
}

/**
 * Check if listing matches a badge filter.
 */
export function listingMatchesBadgeFilter(listing, badgeId, tagIdToTag) {
  const def = BADGE_FILTERS.find((b) => b.id === badgeId);
  if (!def) return false;
  const listingSlugs = getListingTagSlugs(listing, tagIdToTag);
  const matchSlugs = def.tagSlugs.map(norm);
  return matchSlugs.some((ms) => listingSlugs.some((ls) => ls.includes(ms) || ms.includes(ls)));
}

/**
 * Check if listing matches a philanthropy filter.
 */
export function listingMatchesPhilanthropyFilter(listing, philanthropyId, tagIdToTag) {
  const def = PHILANTHROPY_FILTERS.find((p) => p.id === philanthropyId);
  if (!def) return false;
  const listingSlugs = getListingTagSlugs(listing, tagIdToTag);
  const matchSlugs = def.tagSlugs.map(norm);
  return matchSlugs.some((ms) => listingSlugs.some((ls) => ls.includes(ms) || ms.includes(ls)));
}

/**
 * Check if listing has at least one tag in the given set of tag IDs.
 */
export function listingMatchesCategoryTagIds(listing, selectedTagIds, tagIdToTag) {
  if (!selectedTagIds || selectedTagIds.length === 0) return true;
  const listingTagIds = new Set();
  (listing?.tags ?? []).forEach((t) => {
    if (typeof t === "object" && t !== null && t.id != null) listingTagIds.add(Number(t.id));
    else if (typeof t === "number") listingTagIds.add(t);
  });
  return selectedTagIds.some((id) => listingTagIds.has(Number(id)));
}

/**
 * Build tag id -> tag object map from groupings (for resolving listing.tags when they are IDs).
 */
export function buildTagIdMap(groupings) {
  const map = {};
  if (!Array.isArray(groupings)) return map;
  groupings.forEach((g) => {
    (g.tags || []).forEach((t) => {
      if (t && (t.id != null || t.slug)) map[t.id] = t;
    });
  });
  return map;
}

/**
 * Apply all active filters to a list of listings (AND across groups, OR within group).
 * @param {Object[]} listings
 * @param {Object} filterState - { search, valueIds, badgeIds, philanthropyIds, categoryTagIds }
 * @param {Object} tagIdToTag
 */
export function applyListingsFilters(listings, filterState, tagIdToTag) {
  if (!Array.isArray(listings)) return [];
  let out = listings;

  const { search = "", valueIds = [], badgeIds = [], philanthropyIds = [], categoryTagIds = [] } = filterState;
  const searchNorm = search.trim().toLowerCase();

  if (searchNorm) {
    out = out.filter((l) => {
      const name = (l.business_name || "").toLowerCase();
      const desc = (l.short_description || "").toLowerCase();
      const tagNames = (l.tags || [])
        .map((t) => (typeof t === "object" && t?.name ? t.name : tagIdToTag[t]?.name))
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return name.includes(searchNorm) || desc.includes(searchNorm) || tagNames.includes(searchNorm);
    });
  }

  if (valueIds.length > 0) {
    out = out.filter((l) => valueIds.some((id) => listingMatchesValueFilter(l, id, tagIdToTag)));
  }
  if (badgeIds.length > 0) {
    out = out.filter((l) => badgeIds.some((id) => listingMatchesBadgeFilter(l, id, tagIdToTag)));
  }
  if (philanthropyIds.length > 0) {
    out = out.filter((l) => philanthropyIds.some((id) => listingMatchesPhilanthropyFilter(l, id, tagIdToTag)));
  }
  if (categoryTagIds.length > 0) {
    out = out.filter((l) => listingMatchesCategoryTagIds(l, categoryTagIds, tagIdToTag));
  }

  return out;
}
